<div class="dd-page" id="dd-page">
  <div class="dd-page__inner">
    <header class="dd-hero">
      <h1>Credit Due Diligence</h1>
      <p class="dd-hero__lede">
        News-based credit risk assessment · 12-month lookback · one-click Due
        Diligence report
      </p>
    </header>

    <!-- Search step. Kept in the DOM (hidden, not destroyed) while loading:
         the company form owns the in-flight enrichment subscription. -->
    <section
      *ngIf="state !== 'results'"
      [class.dd-hidden]="state === 'loading'"
    >
      <div class="dd-card" id="dd-search-card">
        <h2 class="dd-card__title">
          Search for a company by its official legal name
        </h2>

        <p class="dd-field-hint">
          <mat-icon class="dd-field-hint__icon">info_outline</mat-icon>
          <span>
            Enter the
            <strong>full registered name with its legal form</strong>:
            <span class="dd-tag">AG</span>
            <span class="dd-tag">GmbH</span>
            <span class="dd-tag">S.A.</span>
            <span class="dd-tag">plc</span>
            <span class="dd-tag">Inc.</span>
            <span class="dd-tag">Ltd.</span>
            <br />
            A brand, ticker or shorthand will match the wrong company.
            <button
              type="button"
              class="dd-why"
              id="dd-why-it-matters"
              [class.dd-why--open]="whyOpen"
              [attr.aria-expanded]="whyOpen"
              (click)="whyOpen = !whyOpen"
            >
              Why it matters
              <mat-icon class="dd-why__chev">expand_more</mat-icon>
            </button>
          </span>
        </p>

        <div class="dd-why-box" *ngIf="whyOpen">
          <p class="dd-why-box__text">
            <strong>The report is only as accurate as this one field.</strong>
            Yukka builds it from 12 months of news matched to the entity you
            name here, so the match decides which articles, events and risk
            signals land in the report.
          </p>
          <p class="dd-why-box__text">
            Match the <strong>exact legal entity</strong> and every article is
            about the right company. Enter a brand, ticker or shorthand and you
            pull in subsidiaries, namesakes and unrelated firms, which buries
            real credit events and inflates the signal with noise.
          </p>

          <hr class="dd-why-box__rule" />

          <div class="dd-examples">
            <div class="dd-examples__col dd-examples__col--use">
              <span class="dd-examples__head">Use this · registered name</span>
              <span class="dd-examples__item">
                <mat-icon>check</mat-icon>
                <span>Bayerische Motoren Werke AG</span>
              </span>
              <span class="dd-examples__item">
                <mat-icon>check</mat-icon>
                <span>Nestlé S.A.</span>
              </span>
              <span class="dd-examples__item">
                <mat-icon>check</mat-icon>
                <span>Volkswagen AG</span>
              </span>
            </div>
            <div class="dd-examples__col dd-examples__col--avoid">
              <span class="dd-examples__head"
                >Not this · brand / shorthand</span
              >
              <span class="dd-examples__item dd-examples__item--strike">
                <mat-icon>close</mat-icon>
                <span>BMW</span>
              </span>
              <span class="dd-examples__item dd-examples__item--strike">
                <mat-icon>close</mat-icon>
                <span>Nestle</span>
              </span>
              <span class="dd-examples__item dd-examples__item--strike">
                <mat-icon>close</mat-icon>
                <span>VW · VOW3.DE</span>
              </span>
            </div>
          </div>
        </div>

        <label class="dd-field-label">
          Official legal name <span class="dd-field-label__req">*</span>
        </label>

        <app-company-form
          [nativeInput]="true"
          panelClass="dd-overlay-panel"
          legalNamePlaceholder="e.g. Volkswagen AG"
          [userEmail]="userEmail"
          [project]="project"
          (submitForm)="onCompanySubmit($event)"
          (navigateToEntity)="onNavigateToEntity($event)"
          (searchStateChange)="onSearchStateChange($event)"
        ></app-company-form>

        <p class="dd-form-hint">
          Copy it exactly as in the register, filing or loan agreement,
          punctuation and accents included.
        </p>

        <hr class="dd-divider" />

        <div class="dd-error" *ngIf="errorMessage">{{ errorMessage }}</div>

        <div class="dd-card__actions">
          <button
            mat-raised-button
            color="primary"
            id="dd-run-analysis"
            [disabled]="!companySearchEnabled"
            (click)="startSearch()"
          >
            Search
          </button>
        </div>
      </div>

      <div
        class="dd-recents"
        id="dd-recent-searches"
        *ngIf="recents.length > 0"
      >
        <div class="dd-recents__head">
          <span class="dd-recents__title">Recent analyses</span>
          <button
            type="button"
            class="dd-recents__clear"
            id="dd-recents-clear"
            (click)="clearRecents()"
          >
            Clear
          </button>
        </div>
        <button
          type="button"
          class="dd-recents__row"
          *ngFor="let entry of recents"
          (click)="openRecent(entry)"
        >
          <mat-icon>history</mat-icon>
          <span class="dd-recents__name">{{ entry.data.name }}</span>
          <span class="dd-recents__time">{{
            entry.savedAt | date: 'mediumDate'
          }}</span>
          <mat-icon class="dd-recents__chev">chevron_right</mat-icon>
        </button>
      </div>
    </section>

    <!-- Loading -->
    <section
      class="dd-card dd-card--loading"
      id="dd-loading"
      *ngIf="state === 'loading'"
    >
      <app-ai-loading-card
        [phase]="loadingPhase"
        [subject]="loadingSubject"
        [showStages]="true"
        (cancelLoading)="cancelLoading()"
      ></app-ai-loading-card>
    </section>

    <!-- Results -->
    <section id="dd-results" *ngIf="state === 'results' && resultData">
      <div class="dd-card">
        <div class="dd-actions">
          <button
            type="button"
            class="dd-back"
            id="dd-back-to-search"
            (click)="resetToSearch()"
          >
            <mat-icon>arrow_back</mat-icon>
            Back to search
          </button>
          <div class="dd-actions__right">
            <button
              type="button"
              class="dd-param-btn"
              id="dd-search-parameters"
              [class.dd-param-btn--open]="paramsOpen"
              [attr.aria-expanded]="paramsOpen"
              (click)="toggleParams()"
            >
              Search parameters
              <mat-icon>{{
                paramsOpen ? 'expand_less' : 'expand_more'
              }}</mat-icon>
            </button>

            <button
              type="button"
              class="dd-param-btn"
              id="dd-save-search"
              [matMenuTriggerFor]="ddSaveMenu"
            >
              <mat-icon>bookmark_border</mat-icon>
              Save search
              <mat-icon>arrow_drop_down</mat-icon>
            </button>
            <mat-menu #ddSaveMenu="matMenu">
              <button
                mat-menu-item
                id="dd-save-to-existing"
                (click)="onSave('add')"
              >
                Add to existing collection
              </button>
              <button
                mat-menu-item
                id="dd-save-to-new"
                (click)="onSave('create')"
              >
                Add to new collection
              </button>
            </mat-menu>
          </div>
        </div>

        <div class="dd-params" *ngIf="paramsOpen">
          <div class="dd-params__bar">
            <span class="dd-params__head">{{
              editing ? 'Edit search parameters' : 'Search parameters'
            }}</span>
            <button
              type="button"
              class="dd-params__edit"
              id="dd-edit-params"
              *ngIf="!editing && resultData?.query"
              (click)="enterEdit()"
            >
              Edit parameters →
            </button>
          </div>

          <!-- View: company + lexicon info -->
          <div class="dd-params__section" *ngIf="!editing">
            <div
              class="dd-params__grid"
              *ngIf="request || lexicon; else noParams"
            >
              <div class="dd-params__cell" *ngIf="lexicon?.name">
                <span class="dd-params__label">Lexicon</span>
                <span class="dd-params__value">{{ lexicon?.name }}</span>
              </div>
              <div class="dd-params__cell" *ngIf="lexicon?.base_form">
                <span class="dd-params__label">Base form</span>
                <span class="dd-params__value">{{ lexicon?.base_form }}</span>
              </div>
              <ng-container *ngIf="request">
                <div class="dd-params__cell">
                  <span class="dd-params__label">Legal name</span>
                  <span class="dd-params__value">{{ request.legal_name }}</span>
                </div>
                <div class="dd-params__cell" *ngIf="request.country">
                  <span class="dd-params__label">Country</span>
                  <span class="dd-params__value">{{ request.country }}</span>
                </div>
                <div class="dd-params__cell" *ngIf="request.city">
                  <span class="dd-params__label">City</span>
                  <span class="dd-params__value">{{ request.city }}</span>
                </div>
                <div class="dd-params__cell" *ngIf="request.sector">
                  <span class="dd-params__label">Sector</span>
                  <span class="dd-params__value">{{ request.sector }}</span>
                </div>
                <div
                  class="dd-params__cell"
                  *ngIf="request.key_persons?.length"
                >
                  <span class="dd-params__label">{{
                    keyPersonLabel(request.key_persons)
                  }}</span>
                  <span class="dd-params__value">{{
                    request.key_persons?.join(', ')
                  }}</span>
                </div>
              </ng-container>
            </div>
            <ng-template #noParams>
              <p class="dd-params__note">
                Parameters aren't stored for a reopened analysis — the generated
                query below is shown from the saved search.
              </p>
            </ng-template>
          </div>

          <!-- Edit: locked entity + editable company parameters -->
          <ng-container *ngIf="editing">
            <div class="dd-edit__field dd-edit__entity">
              <span class="dd-params__label">Entity</span>
              <div class="dd-edit__locked">
                <span>{{ request?.legal_name || resultData.name }}</span>
                <mat-icon
                  class="dd-edit__lock-help"
                  matTooltip="The entity name defines the whole analysis and can't be edited here. To analyse a different company, go back and run a new search."
                  matTooltipPosition="right"
                  >info_outline</mat-icon
                >
              </div>
            </div>

            <ng-container *ngIf="draftRequest">
              <span class="dd-params__head dd-params__head--sub"
                >Company parameters</span
              >
              <div class="dd-edit__params">
                <div class="dd-edit__field">
                  <span class="dd-params__label">Country</span>
                  <input
                    class="dd-edit__input"
                    [value]="draftRequest.country || ''"
                    (change)="
                      setDraftField('country', $any($event.target).value)
                    "
                  />
                </div>
                <div class="dd-edit__field">
                  <span class="dd-params__label">City</span>
                  <input
                    class="dd-edit__input"
                    [value]="draftRequest.city || ''"
                    (change)="setDraftField('city', $any($event.target).value)"
                  />
                </div>
                <div class="dd-edit__field">
                  <span class="dd-params__label">Sector</span>
                  <select
                    class="dd-edit__select"
                    (change)="
                      setDraftField('sector', $any($event.target).value)
                    "
                  >
                    <option value="" [selected]="!draftRequest.sector">
                      —
                    </option>
                    <option
                      *ngIf="
                        draftRequest.sector &&
                        !sectorOptions.includes(draftRequest.sector)
                      "
                      [value]="draftRequest.sector"
                      [selected]="true"
                    >
                      {{ draftRequest.sector }}
                    </option>
                    <option
                      *ngFor="let s of sectorOptions"
                      [value]="s"
                      [selected]="s === draftRequest.sector"
                    >
                      {{ s }}
                    </option>
                  </select>
                </div>
                <div class="dd-edit__field">
                  <span class="dd-params__label">Website</span>
                  <input
                    class="dd-edit__input"
                    [value]="draftRequest.website || ''"
                    (change)="
                      setDraftField('website', $any($event.target).value)
                    "
                  />
                </div>
              </div>

              <div class="dd-edit__field dd-edit__field--wide">
                <span class="dd-params__label">{{
                  keyPersonLabel(draftList('key_persons'))
                }}</span>
                <div class="dd-query-pills dd-edit__list">
                  <span
                    class="dd-pill"
                    *ngFor="
                      let person of draftList('key_persons');
                      index as i;
                      trackBy: trackByIndex
                    "
                  >
                    <input
                      class="dd-pill__input"
                      [value]="person"
                      [style.width]="pillWidth(person)"
                      (change)="
                        setDraftListItem(
                          'key_persons',
                          i,
                          $any($event.target).value
                        )
                      "
                    />
                    <button
                      type="button"
                      class="dd-pill__ctrl dd-pill__x"
                      (click)="removeDraftListItem('key_persons', i)"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </span>
                  <span class="dd-add">
                    <button
                      type="button"
                      class="dd-add__btn"
                      *ngIf="!isAddOpen('key_persons')"
                      (click)="openAdd('key_persons')"
                    >
                      +
                    </button>
                    <input
                      *ngIf="isAddOpen('key_persons')"
                      cdkFocusInitial
                      class="dd-add__inline"
                      [(ngModel)]="addDraft"
                      (keydown.enter)="
                        commitDraftListAdd('key_persons');
                        $event.preventDefault()
                      "
                      (blur)="closeAdd()"
                      placeholder="Full name, press Enter"
                    />
                  </span>
                </div>
                <span class="dd-edit__sub"
                  >From enrichment, CEO first — the order is meaningful.</span
                >
              </div>

              <!-- Reviewer additions: the only two call-2 fields the cockpit
                   owns. Everything else in the request is server-curated. -->
              <div class="dd-edit__field dd-edit__field--wide">
                <span class="dd-params__label">Exclusion terms</span>
                <div class="dd-query-pills dd-edit__list">
                  <span
                    class="dd-pill dd-pill--negated"
                    *ngFor="
                      let seed of draftList('exclusion_seeds');
                      index as i;
                      trackBy: trackByIndex
                    "
                  >
                    <input
                      class="dd-pill__input"
                      [value]="seed"
                      [style.width]="pillWidth(seed)"
                      (change)="
                        setDraftListItem(
                          'exclusion_seeds',
                          i,
                          $any($event.target).value
                        )
                      "
                    />
                    <button
                      type="button"
                      class="dd-pill__ctrl dd-pill__x"
                      (click)="removeDraftListItem('exclusion_seeds', i)"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </span>
                  <span class="dd-add">
                    <button
                      type="button"
                      class="dd-add__btn"
                      *ngIf="!isAddOpen('exclusion_seeds')"
                      (click)="openAdd('exclusion_seeds')"
                    >
                      +
                    </button>
                    <input
                      *ngIf="isAddOpen('exclusion_seeds')"
                      cdkFocusInitial
                      class="dd-add__inline"
                      [(ngModel)]="addDraft"
                      (keydown.enter)="
                        commitDraftListAdd('exclusion_seeds');
                        $event.preventDefault()
                      "
                      (blur)="closeAdd()"
                      placeholder="Namesake or sponsorship, press Enter"
                    />
                  </span>
                </div>
                <span class="dd-edit__sub"
                  >Keeps namesakes and sponsorships out. A term that appears
                  inside one of the query's own name terms is ignored.</span
                >
              </div>
              <div class="dd-edit__field dd-edit__field--wide">
                <span class="dd-params__label">Guidance</span>
                <textarea
                  class="dd-edit__input dd-edit__textarea"
                  rows="2"
                  placeholder="e.g. Focus on insolvency and supply-chain coverage."
                  [value]="draftRequest.message || ''"
                  (change)="setDraftField('message', $any($event.target).value)"
                ></textarea>
                <span class="dd-edit__sub"
                  >Free text passed to the query model.</span
                >
              </div>

              <p class="dd-edit__hint">
                Editing a company field re-generates the query from
                generate/company on re-run. Identifiers, exchanges, indexes and
                name forms come from enrichment and are sent unchanged.
              </p>
            </ng-container>
          </ng-container>

          <!-- Generated query — shared rows (view + edit) -->
          <div
            class="dd-query"
            [class.dd-query--edit]="editing"
            *ngIf="queryBadges.terms || editing"
          >
            <div class="dd-query__head">
              <div class="dd-query__title">
                <span class="dd-params__head dd-params__head--sub"
                  >Generated query</span
                >
                <span class="dd-chip dd-chip--inc"
                  >{{ queryBadges.terms }} terms</span
                >
                <span class="dd-chip dd-chip--exc" *ngIf="queryBadges.excluded"
                  >{{ queryBadges.excluded }} excluded</span
                >
              </div>
            </div>

            <app-query-builder
              [tree]="activeQuery"
              [lexicon]="lexicon"
              [editing]="editing"
              [fields]="pickerFields"
              naming="role"
              panelClass="dd-overlay-panel"
              (treeChange)="onQueryChange($event)"
            ></app-query-builder>

            <p class="dd-edit__hint" *ngIf="editing">
              Click a term to rename · ✕ removes it.
            </p>
          </div>

          <!-- Edit footer -->
          <div class="dd-edit__footer" *ngIf="editing">
            <span
              class="dd-edit__diff"
              [class.dd-edit__diff--warn]="
                hasIncompleteGrounded || regenerateDiscardsQuery
              "
              >{{ editNote }}</span
            >
            <div class="dd-edit__actions">
              <button
                type="button"
                class="dd-edit__reset"
                (click)="resetToGenerated()"
              >
                Reset to generated
              </button>
              <button
                type="button"
                class="dd-edit__cancel"
                id="dd-edit-cancel"
                (click)="cancelEdit()"
              >
                Cancel
              </button>
              <button
                type="button"
                class="dd-edit__rerun"
                id="dd-rerun-analysis"
                [disabled]="!changedKeys.length || rerunning"
                (click)="rerun()"
              >
                Re-run analysis
              </button>
            </div>
          </div>
        </div>

        <div class="dd-rerun-bar" *ngIf="rerunning">
          <span class="dd-rerun-bar__spinner"></span>
          Re-running analysis…
        </div>

        <!-- A failed re-run keeps the previous results on screen, so it needs its
             own message here: the search-step one is not rendered. -->
        <div class="dd-error" *ngIf="errorMessage && !rerunning">
          {{ errorMessage }}
        </div>

        <div class="dd-results-head">
          <div class="dd-results-head__text">
            <span class="dd-eyebrow-row">
              <span class="dd-eyebrow"
                >Due diligence · {{ timeframeLabel }}</span
              >
              <span class="dd-rerun-chip" *ngIf="rerunAt">
                Re-run {{ rerunAt | date: 'dd MMM yyyy, HH:mm' }}
              </span>
            </span>
            <h2>
              <button
                type="button"
                class="dd-results-head__link"
                id="dd-open-full-analysis"
                (click)="openFullResults()"
              >
                {{ resultData.name }}
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </h2>
            <p class="dd-results-head__desc" *ngIf="resultData.description">
              {{ resultData.description }}
            </p>
          </div>
          <div class="dd-results-head__cta" id="dd-report-cta">
            <app-report-box
              *ngIf="!config.appConfig.setup.reportsDisabled"
              mode="query"
              buttonLabel="Report"
              buttonLabelPrefix="Due Diligence"
            ></app-report-box>
          </div>
        </div>

        <div class="dd-tiles">
          <div class="dd-tile" [class.dd-tile--pending]="tilesLoading">
            <span class="dd-tile__label">Sentiment</span>
            <span
              class="dd-tile__value"
              [style.color]="
                tiles.sentiment !== null ? (tiles.sentiment | color) : null
              "
            >
              {{
                tiles.sentiment !== null
                  ? (tiles.sentiment | percent: '1.0-0')
                  : '—'
              }}
            </span>
            <span class="dd-tile__delta" *ngIf="deltas?.sentiment != null">{{
              deltaLabel(deltas.sentiment)
            }}</span>
          </div>
          <div class="dd-tile" [class.dd-tile--pending]="tilesLoading">
            <span class="dd-tile__label">Detected events</span>
            <span class="dd-tile__value">{{
              tiles.eventsTotal !== null
                ? (tiles.eventsTotal | thousandSuff)
                : '—'
            }}</span>
            <span class="dd-tile__delta" *ngIf="deltas?.events != null">{{
              deltaLabel(deltas.events)
            }}</span>
          </div>
          <div class="dd-tile" [class.dd-tile--pending]="tilesLoading">
            <span class="dd-tile__label">News volume</span>
            <span class="dd-tile__value">{{
              tiles.volumeTotal !== null
                ? (tiles.volumeTotal | thousandSuff)
                : '—'
            }}</span>
            <span class="dd-tile__delta" *ngIf="deltas?.volume != null">{{
              deltaLabel(deltas.volume)
            }}</span>
          </div>
        </div>

        <div class="dd-summary">
          <div class="dd-summary__head">
            <mat-icon>auto_awesome</mat-icon>
            <span>AI Executive Summary</span>
            <span class="dd-summary__badge">Beta</span>
          </div>
          <ng-container *ngIf="summaryLoading; else summaryReady">
            <div class="dd-summary__shimmer"></div>
            <div class="dd-summary__shimmer"></div>
            <div class="dd-summary__shimmer dd-summary__shimmer--short"></div>
          </ng-container>
          <ng-template #summaryReady>
            <p
              class="dd-summary__text"
              *ngFor="let paragraph of summaryParagraphs"
            >
              {{ paragraph }}
            </p>
            <p class="dd-summary__failed" *ngIf="summaryFailed">
              We couldn't generate the summary.
              <button
                type="button"
                id="dd-summary-retry"
                (click)="retrySummary()"
              >
                Retry
              </button>
            </p>
          </ng-template>
        </div>

        <div class="dd-events" *ngIf="tiles.topEvents.length > 0">
          <h3 class="dd-events__title">Top detected events</h3>
          <div class="dd-events__row" *ngFor="let row of tiles.topEvents">
            <span class="dd-events__name">{{ row.name }}</span>
            <span class="dd-events__bar">
              <i [style.width]="row.barWidth"></i>
            </span>
            <span class="dd-events__count">{{ row.count | number }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
